<template>
	<div
		class="inline-flex shrink-0 items-center"
		:class="{
			'gap-1': size === 'sm' || size === 'xs' || size === 'md',
			'gap-2': size === 'lg'
		}"
	>
		<div
			class="text-label-primary"
			:class="{
				'text-xs font-medium': size === 'xs',
				'text-sm font-medium': size === 'sm',
				'text-sm font-semibold': size === 'md',
				'font-squad text-[32px] leading-10.5 font-black': size === 'lg'
			}"
			v-if="price < 0"
		>
			-
		</div>
		<img
			v-if="!noCoin"
			:src="CoinIcon"
			:class="{
				'h-5 w-5': size === 'xs' || size === 'sm' || size === 'md',
				'h-8 w-8': size === 'lg',
				'opacity-30 grayscale-100': disabled
			}"
		/>
		<div
			class="text-label-primary"
			:class="{
				'text-xs font-medium': size === 'xs',
				'text-sm font-medium': size === 'sm',
				'text-sm font-semibold': size === 'md',
				'font-squad text-[32px] leading-10.5 font-black': size === 'lg',
				'inline-flex *:text-center': mono
			}"
			v-html="priceFormatted"
		></div>
	</div>
</template>
<script setup lang="ts">
import CoinIcon from '@/assets/icons/coin.svg?url'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
	defineProps<{
		price: number
		size?: 'xs' | 'sm' | 'md' | 'lg'
		minDigits?: number
		mono?: boolean
		animationDuration?: number
		shorten?: boolean
		prefix?: string
		suffix?: string
		noCoin?: boolean
		disabled?: boolean
	}>(),
	{
		size: 'sm',
		minDigits: 0
	}
)

const monoSize = computed(() => {
	if (props.size === 'xs') {
		return 'w-1.5'
	}
	if (props.size === 'sm') {
		return 'w-2.125'
	}
	if (props.size === 'md') {
		return 'w-2.25'
	}

	return 'w-5'
})

const localPrice = ref(0)

let start = 0
const createAnimationFrame = (from: number, to: number, duration: number) => {
	start = performance.now()
	const animate = (current: number) => {
		const progress = (current - start) / duration
		localPrice.value = from + (to - from) * progress
		if (progress < 1) {
			requestAnimationFrame(animate)
		} else {
			localPrice.value = to
		}
	}
	requestAnimationFrame(animate)
}

watch(
	() => props.price,
	() => {
		if (props.animationDuration) {
			return createAnimationFrame(
				localPrice.value,
				props.price,
				props.animationDuration
			)
		}

		localPrice.value = props.price
	},
	{ immediate: true }
)

const priceFormatted = computed(() => {
	let value = localPrice.value / 100
	let suffix = ''

	if (props.shorten) {
		if (value >= 1_000_000) {
			value = value / 1_000_000
			suffix = 'kk'
		} else if (value >= 1_000) {
			value = value / 1_000
			suffix = 'k'
		}
	}

	const parts = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: props.minDigits
	}).formatToParts(value)

	let result =
		(props.prefix ? props.prefix : '') +
		parts
			.filter(
				(part) =>
					part.type === 'integer' ||
					part.type === 'decimal' ||
					part.type === 'fraction' ||
					part.type === 'group'
			)
			.map((part) => part.value)
			.join('') +
		(props.suffix ? props.suffix : suffix)

	if (props.mono) {
		result = result
			.split('')
			.map(
				(char) =>
					`<div ${Number.isInteger(parseInt(char, 10)) ? `class="${monoSize.value}"` : ''}>${char}</div>`
			)
			.join('')
	}

	return result
})
</script>
